import {Navigate, RouteObject} from 'react-router-dom';
import {authGuard} from '@common/auth/guards/auth-route';

const lazyRoute = async (
  cmp: keyof typeof import('@app/backstage/routes/backstage-routes.lazy'),
) => {
  const exports = await import(
    '@app/backstage/routes/backstage-routes.lazy'
  );
  return {
    Component: exports[cmp],
  };
};

export const backstageRoutes: RouteObject[] = [
  // Backstage request form
  {
    loader: () => authGuard({permission: 'backstageRequests.create'}),
    children: [
      {
        path: 'backstage/requests',
        lazy: () => lazyRoute('BackstageRequestSelector'),
      },
      {
        path: 'backstage/requests/become-artist',
        lazy: () => lazyRoute('BackstageRequestFormPage'),
      },
      {
        path: 'backstage/requests/become-creator',
        lazy: () => lazyRoute('BackstageRequestFormPage'),
      },
      {
        path: 'backstage/requests/become-author',
        lazy: () => lazyRoute('BackstageRequestFormPage'),
      },
      {
        path: 'backstage/requests/become-label',
        lazy: () => lazyRoute('BackstageRequestFormPage'),
      },
      {
        path: 'backstage/requests/become-distributor',
        lazy: () => lazyRoute('BackstageRequestFormPage'),
      },
      // {
      //   path: 'backstage/requests/verify-artist',
      //   loader: () => authGuard({permission: 'artist.verify'}),
      //   lazy: () => lazyRoute('BackstageRequestFormPage'),
      // },
      // {
      //   path: 'backstage/requests/claim-artist',
      //   loader: () => authGuard({permission: 'artist.claim'}),
      //   lazy: () => lazyRoute('BackstageRequestFormPage'),
      // },
      {
        path: 'backstage/requests/:requestId/request-submitted',
        lazy: () => lazyRoute('BackstageRequestSubmittedPage'),
      },
    ],
  },

  {
    path: 'backstage',
    loader: () => authGuard({permission: 'backstage.access', redirectUrl: "/"}),
    lazy: () => lazyRoute('BackstageHomePage'),
    children: [
      {
        index: true,
        element: <Navigate to="/backstage/insights" replace />,
      },
    ],
  },

  {
    path: 'backstage/upload',
    loader: () => authGuard({permission: 'medias.create', role: ['artists', 'creators', 'authors', 'labels', 'distributors'], redirectUrl: "/"}),
    lazy: () => lazyRoute('BackstageUploadPage'),
  },
  {
    path: 'backstage/quota',
    loader: () =>
      authGuard({
        role: ['artists', 'creators', 'authors', 'labels', 'distributors'],
        redirectUrl: "/",
      }),
    lazy: () => lazyRoute('BackstageQuotaPage'),
  },

  // Tracks
  {
    path: 'backstage/tracks/new',
    loader: () => authGuard({permission: 'medias.create', role: ['artists', 'creators', 'authors', 'labels', 'distributors'], redirectUrl: "/"}),
    lazy: () => lazyRoute('BackstageCreateTrackPage'),
  },
  {
    path: 'backstage/tracks/:trackId/edit',
    loader: () => authGuard({role: ['artists', 'creators', 'authors', 'labels', 'distributors'], redirectUrl: "/"}),
    lazy: () => lazyRoute('BackstageUpdateTrackPage'),
  },
  {
    path: 'backstage/tracks/:trackId/insights',
    loader: () => authGuard({role: ['artists', 'creators', 'authors', 'labels', 'distributors'], redirectUrl: "/"}),
    lazy: () => lazyRoute('BackstageTrackInsights'),
  },

  // Albums
  {
    path: 'backstage/albums',
    loader: () =>
      authGuard({
        role: ['artists', 'creators', 'authors', 'labels', 'distributors'],
        redirectUrl: "/",
      }),
    lazy: () => lazyRoute('BackstageAlbumsPage'),
  },
  {
    path: 'backstage/albums/new',
    loader: () => authGuard({permission: 'medias.create', role: ['artists', 'creators', 'authors', 'labels', 'distributors'], redirectUrl: "/"}),
    lazy: () => lazyRoute('BackstageCreateAlbumPage'),
  },
  {
    path: 'backstage/albums/:albumId/edit',
    loader: () => authGuard({role: ['artists', 'creators', 'authors', 'labels', 'distributors'], redirectUrl: "/"}),
    lazy: () => lazyRoute('BackstageUpdateAlbumPage'),
  },
  {
    path: 'backstage/albums/:albumId/insights',
    loader: () => authGuard({permission: 'backstage.insights', redirectUrl: "/"}),
    lazy: () => lazyRoute('BackstageAlbumInsights'),
  },

  // Artists
  {
    path: 'backstage/profil',
    loader: () =>
      authGuard({
        role: ['artists', 'creators', 'authors', 'labels', 'distributors'],
        redirectUrl: "/",
      }),
    lazy: () => lazyRoute('BackstageArtistProfilePage'),
  },
  {
    path: 'backstage/profil/:artistId/edit',
    loader: () =>
      authGuard({
        role: ['artists', 'creators', 'authors', 'labels', 'distributors'],
        redirectUrl: "/",
      }),
    lazy: () => lazyRoute('BackstageArtistProfilePage'),
  },
  {
    path: 'backstage/profile',
    loader: () =>
      authGuard({
        role: ['artists', 'creators', 'authors', 'labels', 'distributors'],
        redirectUrl: "/",
      }),
    lazy: () => lazyRoute('BackstageArtistProfilePage'),
  },
  {
    path: 'backstage/profile/:artistId',
    loader: () =>
      authGuard({
        role: ['artists', 'creators', 'authors', 'labels', 'distributors'],
        redirectUrl: "/",
      }),
    lazy: () => lazyRoute('BackstageArtistProfilePage'),
  },
  {
    path: 'backstage/artists/:artistId/edit',
    loader: () => authGuard({permission: 'backstage.insights', redirectUrl: "/"}),
    lazy: () => lazyRoute('BackstageUpdateArtistPage'),
  },
  {
    path: 'backstage/artists/:artistId/insights',
    loader: () => authGuard({permission: 'backstage.insights', redirectUrl: "/"}),
    lazy: () => lazyRoute('BackstageArtistInsights'),
  },

  // Insights
  {
    path: 'backstage/insights',
    loader: () => authGuard({permission: 'backstage.insights', redirectUrl: "/"}),
    lazy: () => lazyRoute('BackstageGlobalInsights'),
  },

  // Live Events
  {
    path: 'backstage/live-events',
    loader: () => authGuard({redirectUrl: "/"}),
    lazy: () => lazyRoute('BackstageLiveEventsDataTable'),
  },
  {
    path: 'backstage/live-events/new',
    loader: () => authGuard({redirectUrl: "/"}),
    lazy: () => lazyRoute('LiveEventPageCreateBackstage'),
  },
  {
    path: 'backstage/live-events/:slugOrId/edit',
    loader: () => authGuard({redirectUrl: "/"}),
    lazy: () => lazyRoute('LiveEventPageUpdateBackstage'),
  },
  {
    path: 'backstage/live-events/:slugOrId/access',
    loader: () => authGuard({redirectUrl: "/"}),
    lazy: () => lazyRoute('LiveEventAccessPageBackstage'),
  },
  {
    path: 'backstage/live-events/:slugOrId/tickets',
    loader: () => authGuard({redirectUrl: "/"}),
    lazy: () => lazyRoute('LiveEventsTicketsDataTablePageBackstage'),
  },
  {
    path: 'backstage/live-events/:slugOrId/tickets/new',
    loader: () =>
      authGuard({
        permission: 'live_event.tickets.create',
        redirectUrl: "/",
      }),
    lazy: () => lazyRoute('LiveEventTicketPageCreateBackstage'),
  },
  {
    path: 'backstage/live-events/:slugOrId/tickets/:ticketId/edit',
    loader: () => authGuard({redirectUrl: "/"}),
    lazy: () => lazyRoute('LiveEventTicketPageUpdateBackstage'),
  },
  {
    path: 'backstage/live-events/:slugOrId/scan-sessions',
    loader: () => authGuard({redirectUrl: "/"}),
    lazy: () => lazyRoute('LiveEventsScanSessionDataTablePageBackstage'),
  },
  {
    path: 'backstage/live-events/:slugOrId/scan-sessions/new',
    loader: () => authGuard({redirectUrl: "/"}),
    lazy: () => lazyRoute('LiveEventScanSessionPageCreateBackstage'),
  },
  {
    path: 'backstage/live-events/:slugOrId/scan-sessions/:sessionId/edit',
    loader: () => authGuard({redirectUrl: "/"}),
    lazy: () => lazyRoute('LiveEventScanSessionPageUpdateBackstage'),
  },
  {
    path: 'backstage/live-events/:slugOrId/sell',
    loader: () => authGuard({redirectUrl: "/"}),
    lazy: () => lazyRoute('BackstageLiveEventPos'),
  },
  {
    path: 'backstage/live-events/:slugOrId/scan',
    loader: () => authGuard({redirectUrl: "/"}),
    lazy: () => lazyRoute('BackstageLiveEventScanSession'),
  }
];
